/**
 * Tema claro / oscuro de la instancia.
 *
 * Baserow no tiene modo oscuro: es una aplicacion clara y ni la edicion de pago
 * trae un interruptor. Lo que hay es CSS nuestro. `branding/build-theme.py`
 * publica el CSS de Baserow tal cual (o sea, el tema claro) y encima cuelga
 * todos los colores del tema oscuro de `html[data-tema="oscuro"]`. Aqui solo se
 * decide que dice ese atributo.
 *
 * La preferencia vive en localStorage, no en el perfil del usuario. Es lo que
 * hace todo el mundo con un interruptor de tema, y evita una migracion, un
 * endpoint y un viaje al servidor antes de poder pintar. El costo es que la
 * eleccion es por navegador: si Victor entra desde otra maquina, arranca en
 * "sistema" otra vez.
 */
export const CLAVE = 'mg-tema'
export const MODOS = ['sistema', 'claro', 'oscuro']

/**
 * La piel: como se ve la interfaz por encima del tema.
 *
 * 'monday' es la de fabrica en esta instancia — el equipo viene de Monday y la
 * idea es que no sienta que cambio de herramienta. 'baserow' la apaga y deja la
 * aplicacion tal como viene.
 *
 * Es aparte del tema a proposito: la piel cambia formas, medidas y tipografia;
 * el tema decide claro u oscuro. Mezclarlos obligaria a mantener cuatro
 * combinaciones en el CSS en vez de dos cosas independientes.
 */
export const CLAVE_PIEL = 'mg-piel'
export const PIELES = ['monday', 'baserow']
export const PIEL_POR_DEFECTO = 'monday'

export function leerPiel() {
  try {
    const v = window.localStorage.getItem(CLAVE_PIEL)
    return PIELES.includes(v) ? v : PIEL_POR_DEFECTO
  } catch (e) {
    return PIEL_POR_DEFECTO
  }
}

export function aplicarPiel(piel) {
  document.documentElement.setAttribute('data-piel', piel)
  // La piel de Monday es clara —Monday no tiene modo oscuro— y mezclarla con
  // el tema oscuro deja media pantalla en negro y media en blanco. Con la piel
  // puesta, el tema que manda es el claro; el oscuro sigue disponible apagando
  // la piel desde el mismo menu.
  if (piel === 'monday') {
    document.documentElement.setAttribute('data-tema', 'claro')
  } else {
    aplicar(leerModo())
  }
}

export function guardarPiel(piel) {
  try {
    window.localStorage.setItem(CLAVE_PIEL, piel)
  } catch (e) {
    // Sin almacenamiento la eleccion dura lo que la pestana.
  }
  aplicarPiel(piel)
}

/** Lo que el usuario eligio, o 'sistema' si no ha elegido nada. */
export function leerModo() {
  try {
    const v = window.localStorage.getItem(CLAVE)
    return MODOS.includes(v) ? v : 'sistema'
  } catch (e) {
    // localStorage revienta en ventanas privadas y con las cookies bloqueadas.
    // Que no se pueda recordar la eleccion no es motivo para tumbar la app.
    return 'sistema'
  }
}

/** El tema que toca pintar de verdad: resuelve 'sistema' contra el navegador. */
export function resolver(modo) {
  if (modo === 'claro' || modo === 'oscuro') {
    return modo
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'oscuro'
      : 'claro'
  } catch (e) {
    return 'claro'
  }
}

/**
 * Escribe el atributo del que cuelga el CSS.
 *
 * Se pone SIEMPRE, tambien en claro. Podria omitirse —el CSS base ya es el
 * claro— pero dejarlo explicito permite que las reglas del logo usen
 * :not([data-tema="oscuro"]) y sigan valiendo aunque este script no llegue a
 * correr.
 */
export function aplicar(modo) {
  // Con la piel de Monday puesta el tema es siempre claro (ver aplicarPiel).
  if (leerPiel() === 'monday') {
    document.documentElement.setAttribute('data-tema', 'claro')
    return
  }
  document.documentElement.setAttribute('data-tema', resolver(modo))
}

export function guardar(modo) {
  try {
    if (modo === 'sistema') {
      window.localStorage.removeItem(CLAVE)
    } else {
      window.localStorage.setItem(CLAVE, modo)
    }
  } catch (e) {
    // Igual que arriba: sin almacenamiento el tema dura lo que la pestana.
  }
  aplicar(modo)
}

/**
 * El guion que corre en el <head>, antes de que se pinte nada.
 *
 * Va como texto y no como import porque tiene que ejecutarse durante el parseo
 * del documento: si esperara a que hidrate Vue, la pagina aparece un instante en
 * claro y despues salta a oscuro. Ese parpadeo se nota mucho y es justo lo que
 * la gente reporta como "el tema esta roto".
 *
 * Se mantiene diminuto y a prueba de excepciones: si algo aqui truena, la
 * aplicacion entera se queda sin pintar.
 */
export const GUION_CABECERA = `(function(){try{
var p=null;try{p=localStorage.getItem('${CLAVE_PIEL}')}catch(e){}
p=p==='baserow'?'baserow':'${PIEL_POR_DEFECTO}'
document.documentElement.setAttribute('data-piel',p)
var m=null;try{m=localStorage.getItem('${CLAVE}')}catch(e){}
if(m!=='claro'&&m!=='oscuro'){m=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'oscuro':'claro'}
if(p==='monday'){m='claro'}
document.documentElement.setAttribute('data-tema',m)
}catch(e){document.documentElement.setAttribute('data-tema','claro');
document.documentElement.setAttribute('data-piel','${PIEL_POR_DEFECTO}')}})()`
